import { FC, ReactNode, useCallback, useEffect, useState } from 'react';

import { Alert, Button, Descriptions, Empty, Modal, Space, Spin, Tag, Tooltip } from 'antd';

import SchemeEditor from './SchemeEditor';
import {
    IUtmLabelOption,
    IUtmNameOption,
    IUtmParseTask,
    IUtmScheme,
    IUtmSchemesSummary,
    PARSER_TYPE_LABELS,
    TASK_STATUS,
    pluralizeValues,
} from './types';
import { useParseTask } from './useParseTask';
import { formatNumberWithSpaces } from '../../../../../helpers/utils/functions';
import { getApiErrorMessage, showSuccessMessage } from '../../../../../helpers/utils/ui';
import { utmSchemesApi } from '../../../../../shared/common/api/endpoints/utmSchemesApi';
import { useTypedSelector } from '../../../../../shared/common/hooks/useTypedSelector';

interface ISchemesModalProps {
    visible: boolean;
    cabinetId: number;
    cabinetName: string;
    onClose: () => void;
    /**
     * Схема изменилась. В аргументе — свежая сводка кабинета: таблица обновляет
     * свою строку сразу, не дожидаясь ответа списка кабинетов.
     */
    onChanged: (summary?: IUtmSchemesSummary) => void;
}

/** Сводка строки таблицы кабинетов по загруженной схеме: у кабинета она одна */
const summaryOfScheme = (scheme: IUtmScheme | null): IUtmSchemesSummary => ({
    count: scheme ? 1 : 0,
    active: scheme && scheme.is_active ? 1 : 0,
    parsed_values: scheme?.stats?.parsed_values ?? 0,
    names: scheme ? [ scheme.name ] : [],
});

/**
 * Кнопка действия со схемой. Заблокированная кнопка объясняет причину: у antd
 * disabled-кнопка не отдаёт события мыши, поэтому подсказку вешаем на обёртку.
 */
const ActionButton: FC<{
    onClick: () => void;
    disabled?: boolean;
    loading?: boolean;
    danger?: boolean;
    hint?: string;
    children: ReactNode;
}> = ({ onClick, disabled, loading, danger, hint, children }) => {
    const button = (
        <Button onClick={onClick} disabled={disabled} loading={loading} danger={danger}>
            {children}
        </Button>
    );

    if (!disabled || !hint) return button;

    return (
        <Tooltip title={hint}>
            <span style={{ display: 'inline-block', cursor: 'not-allowed' }}>
                <span style={{ pointerEvents: 'none' }}>{button}</span>
            </span>
        </Tooltip>
    );
};

/** Подпись статуса задачи переразбора */
const taskLabel = (task: IUtmParseTask): string => {
    switch (task.status) {
        case TASK_STATUS.pending:
            return 'Переразбор в очереди';
        case TASK_STATUS.running:
            return 'Идёт переразбор';
        case TASK_STATUS.done: {
            const inserted = task.result?.inserted ?? 0;
            return `Переразбор завершён: ${formatNumberWithSpaces(inserted)} ${pluralizeValues(inserted)}`;
        }
        default:
            return task.result?.error
                ? `Переразбор не удался: ${task.result.error}`
                : 'Переразбор не удался';
    }
};

/**
 * Окно настройки разбора параметров кабинета.
 *
 * У кабинета одна схема: показываем её состояние и действия — включить или выключить,
 * переразобрать историю, удалить. Пока по школе идёт задача переразбора, действия
 * заблокированы: вторую задачу бэкенд не примет (409).
 * Редактор схемы открывается по кнопке: ссылка → поля → предпросмотр.
 */
const SchemesModal: FC<ISchemesModalProps> = ({
    visible,
    cabinetId,
    cabinetName,
    onClose,
    onChanged,
}) => {
    const { selectedSchool } = useTypedSelector((state) => state.currentSchool);
    const schoolId = selectedSchool?.id;

    const [ loading, setLoading ] = useState(false);
    const [ acting, setActing ] = useState(false);
    const [ error, setError ] = useState('');
    const [ scheme, setScheme ] = useState<IUtmScheme | null>(null);
    const [ schoolTaskPending, setSchoolTaskPending ] = useState(false);
    // Справочники для редактора приходят вместе со списком схем
    const [ utmNames, setUtmNames ] = useState<IUtmNameOption[]>([]);
    const [ labels, setLabels ] = useState<IUtmLabelOption[]>([]);
    const [ editorVisible, setEditorVisible ] = useState(false);

    const loadScheme = useCallback(async (): Promise<IUtmScheme | null> => {
        if (!schoolId || !cabinetId) return null;

        setLoading(true);
        setError('');
        try {
            const response = await utmSchemesApi.getList(schoolId, cabinetId);
            const payload = response.data?.data ?? {};
            // Схема у кабинета одна; бэкенд отдаёт список, берём первую
            const cabinetSchemes = Array.isArray(payload.cabinet) ? payload.cabinet : [];
            const current = (cabinetSchemes[0] ?? null) as IUtmScheme | null;

            setScheme(current);
            setSchoolTaskPending(Boolean(payload.school_task_pending));
            setUtmNames(Array.isArray(payload.utm_names) ? payload.utm_names : []);
            setLabels(Array.isArray(payload.labels) ? payload.labels : []);
            return current;
        } catch (err) {
            setError(getApiErrorMessage(err, 'Не удалось загрузить настройку разбора'));
            return null;
        } finally {
            setLoading(false);
        }
    }, [ schoolId, cabinetId ]);

    // После завершения задачи обновляем и схему, и сводку в таблице кабинетов
    const handleTaskFinished = useCallback(() => {
        loadScheme().then((current) => onChanged(summaryOfScheme(current)));
    }, [ loadScheme, onChanged ]);

    const {
        task,
        error: taskError,
        timedOut,
        isRunning,
        watch,
        reset,
    } = useParseTask(schoolId, handleTaskFinished);

    useEffect(() => {
        if (!visible) {
            reset();
            return;
        }
        // При открытии возобновляем слежение за незавершённой задачей прошлой сессии
        loadScheme().then((current) => watch(current?.stats?.last_task ?? null));
    }, [ visible, loadScheme, watch, reset ]);

    /** Общая обёртка действий: блокировка кнопок, единый разбор ошибок, обновление данных */
    const runAction = async (
        action: () => Promise<{ task: IUtmParseTask | null; message: string }>,
        fallbackError: string
    ) => {
        setActing(true);
        setError('');
        try {
            const { task: nextTask, message } = await action();
            showSuccessMessage(message);
            const current = await loadScheme();
            onChanged(summaryOfScheme(current));
            if (nextTask) {
                watch(nextTask);
            }
        } catch (err) {
            setError(getApiErrorMessage(err, fallbackError));
        } finally {
            setActing(false);
        }
    };

    const applyActive = (nextActive: boolean) => {
        if (!scheme || !schoolId) return;

        runAction(async () => {
            // Переразбор нужен именно при выключении: строки схемы должны уйти другим схемам
            const response = await utmSchemesApi.setActive(schoolId, scheme.id, nextActive, !nextActive);
            const taskId = response.data?.data?.task_id;
            return {
                task: taskId ? ({ id: taskId, status: TASK_STATUS.pending } as IUtmParseTask) : null,
                message: nextActive
                    ? 'Схема включена — к прежним значениям она применится после переразбора истории'
                    : 'Схема выключена',
            };
        }, 'Не удалось изменить схему');
    };

    const toggleActive = () => {
        if (!scheme) return;

        // Включение безобидно, а выключение перезапускает разбор всей истории без этой схемы
        if (scheme.is_active) {
            Modal.confirm({
                title: 'Выключить настройку разбора?',
                content: `Схема перестанет применяться. Разобранные ею значения (${formatNumberWithSpaces(scheme.stats?.parsed_values ?? 0)}) `
                    + 'будут переразобраны другими схемами, а параметры, которые больше некому разобрать, '
                    + 'исчезнут из фильтров и группировок отчётов. Сама схема сохранится — её можно включить обратно.',
                okText: 'Выключить',
                cancelText: 'Отмена',
                width: 520,
                onOk: () => applyActive(false),
            });
            return;
        }

        applyActive(true);
    };

    const reparse = () => {
        if (!scheme || !schoolId) return;

        runAction(async () => {
            const response = await utmSchemesApi.reparse(schoolId, scheme.id);
            return {
                task: (response.data?.data ?? null) as IUtmParseTask | null,
                message: 'Переразбор поставлен в очередь',
            };
        }, 'Не удалось поставить переразбор');
    };

    const confirmDelete = () => {
        if (!scheme || !schoolId) return;

        Modal.confirm({
            title: 'Удалить настройку разбора?',
            content: `Схема будет удалена без возможности вернуть — настраивать придётся заново. Разобранные ею значения (${formatNumberWithSpaces(scheme.stats?.parsed_values ?? 0)}) `
                + 'переразберутся другими схемами, а параметры, которые больше некому разобрать, исчезнут из фильтров '
                + 'и группировок отчётов. Пересчёт идёт в фоне и занимает до нескольких минут.',
            width: 520,
            okText: 'Удалить',
            okType: 'danger',
            cancelText: 'Отмена',
            onOk: () =>
                runAction(async () => {
                    const response = await utmSchemesApi.deleteScheme(schoolId, scheme.id);
                    const taskId = response.data?.data?.task_id;
                    return {
                        task: taskId ? ({ id: taskId, status: TASK_STATUS.pending } as IUtmParseTask) : null,
                        message: 'Схема удалена',
                    };
                }, 'Не удалось удалить схему'),
        });
    };

    const parsedValues = scheme?.stats?.parsed_values ?? 0;
    // Пока идёт задача (эта или любая другая по школе), новую бэкенд не примет.
    // Блокировать этим стоит только действия, которые сами ставят задачу:
    // включение схемы задачи не создаёт (reparse: false), удаление без результата — тоже
    const taskBlocked = isRunning || schoolTaskPending;
    const blockedHint = 'Идёт переразбор меток школы — новую задачу можно поставить после его завершения';

    return (
        <Modal
            title={`Настройка параметров в ссылке — ${cabinetName}`}
            visible={visible}
            onCancel={onClose}
            footer={null}
            width={760}
            destroyOnClose
        >
            {error && (
                <Alert type="error" message={error} style={{ marginBottom: 16 }} showIcon />
            )}

            {task && (
                <Alert
                    type={task.status === TASK_STATUS.failed ? 'error' : 'info'}
                    message={taskLabel(task)}
                    description={
                        timedOut
                            ? 'Задача идёт дольше обычного. Откройте окно позже — результат сохранится.'
                            : undefined
                    }
                    style={{ marginBottom: 16 }}
                    showIcon
                />
            )}

            {taskError && (
                <Alert
                    type="warning"
                    message={`Не удаётся получить статус переразбора: ${taskError}`}
                    description="Задача продолжает выполняться. Откройте окно позже, чтобы увидеть результат."
                    style={{ marginBottom: 16 }}
                    showIcon
                />
            )}

            {!task && schoolTaskPending && (
                <Alert
                    type="info"
                    message="По школе идёт переразбор. Пока он не закончится, нельзя запустить новый — включить или изменить схему можно."
                    style={{ marginBottom: 16 }}
                    showIcon
                />
            )}

            <Spin spinning={loading}>
                {scheme ? (
                    <>
                        <Descriptions column={1} bordered size="small">
                            <Descriptions.Item label="Название">
                                {scheme.name}
                                {!scheme.is_active && (
                                    <Tag color="default" style={{ marginLeft: 8 }}>выключена</Tag>
                                )}
                            </Descriptions.Item>
                            <Descriptions.Item label="Тип разбора">
                                {PARSER_TYPE_LABELS[scheme.parser_type] ?? '—'}
                            </Descriptions.Item>
                            <Descriptions.Item label="Разобрано значений">
                                {formatNumberWithSpaces(parsedValues)} {pluralizeValues(parsedValues)}
                            </Descriptions.Item>
                            <Descriptions.Item label="Ссылка-образец">
                                {scheme.sample_url || '—'}
                            </Descriptions.Item>
                        </Descriptions>

                        <Space style={{ marginTop: 16 }} wrap>
                            <Button type="primary" onClick={() => setEditorVisible(true)} disabled={acting}>
                                Изменить схему
                            </Button>

                            <ActionButton
                                onClick={reparse}
                                loading={acting}
                                disabled={acting || taskBlocked || !scheme.is_active}
                                hint={
                                    !scheme.is_active
                                        ? 'Схема выключена — сначала включите её'
                                        : (taskBlocked ? blockedHint : '')
                                }
                            >
                                Переразобрать историю
                            </ActionButton>

                            {/* Включение задачу не ставит, поэтому доступно и во время переразбора */}
                            <ActionButton
                                onClick={toggleActive}
                                disabled={acting || (scheme.is_active && taskBlocked)}
                                hint={scheme.is_active && taskBlocked ? blockedHint : ''}
                            >
                                {scheme.is_active ? 'Выключить' : 'Включить'}
                            </ActionButton>

                            <ActionButton
                                danger
                                onClick={confirmDelete}
                                disabled={acting || (parsedValues > 0 && taskBlocked)}
                                hint={parsedValues > 0 && taskBlocked ? blockedHint : ''}
                            >
                                Удалить
                            </ActionButton>
                        </Space>
                    </>
                ) : (
                    !loading && (
                        <Empty
                            image={Empty.PRESENTED_IMAGE_SIMPLE}
                            description="Разбор параметров для этого кабинета не настроен"
                        >
                            <Button type="primary" onClick={() => setEditorVisible(true)}>
                                Настроить по ссылке
                            </Button>
                        </Empty>
                    )
                )}
            </Spin>

            {editorVisible && (
                <SchemeEditor
                    visible={editorVisible}
                    cabinetId={cabinetId}
                    cabinetName={cabinetName}
                    scheme={scheme}
                    utmNames={utmNames}
                    labels={labels}
                    taskPending={isRunning || schoolTaskPending}
                    onClose={() => setEditorVisible(false)}
                    onSaved={(taskId) => {
                        setEditorVisible(false);
                        loadScheme().then((current) => {
                            onChanged(summaryOfScheme(current));
                            if (taskId) {
                                watch({ id: taskId, status: TASK_STATUS.pending } as IUtmParseTask);
                            }
                        });
                    }}
                />
            )}
        </Modal>
    );
};

export default SchemesModal;
